import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import SvgIcon from "@mui/material/SvgIcon";
import Stack from "@mui/material/Stack";
import { useCallback, useRef, useState } from "react";
import { usePageContext } from "vike-react/usePageContext";
import LogoImage from "../../assets/logo.svg?react";
import { MAX_WIDTH, SIDE_PADDING, PRIMARY_COLOR } from "../../theme/consts";
import { CONTACT_SECTION_ID } from "./Header.config";
import { DesktopNav } from "./DesktopNav";
import { MobileNav } from "./MobileNav";
import {
  getActiveNavPath,
  navigateToPath,
  normalizePath,
  scrollToContactSection,
  scrollToInterestedSection,
  useCloseMenuOnOutsideClick,
  useLockBodyScrollOnMobileMenu,
  useTrackActiveContactSection,
} from "./Header.utils";
import {
  getButtonSx,
  getHeaderSx,
  getMenuButtonSx,
  getNavigationBarSx,
  logoImageSx,
  logoLinkSx,
} from "./Header.sx";

export interface HeaderProps {
  isDarkMode?: boolean;
  isPortfolioPage?: boolean;
  isTeamPage?: boolean;
}

export function Header({
  isDarkMode = false,
  isPortfolioPage = false,
  isTeamPage = false,
}: HeaderProps) {
  const { urlPathname } = usePageContext();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");
  const navigationBarRef = useRef<HTMLDivElement | null>(null);
  const currentPathname = normalizePath(urlPathname ?? "");
  const isOnHomePage = currentPathname === "/" || currentPathname === "";
  const isContactActive = isOnHomePage && activeSection === CONTACT_SECTION_ID;
  const activeNavPath = getActiveNavPath(currentPathname, isOnHomePage, isContactActive);

  const isPageNavItemActive = useCallback(
    (path: string) => activeNavPath === path,
    [activeNavPath],
  );

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleHeaderButtonClick = useCallback(() => {
    if (isPortfolioPage || isTeamPage) {
      // On portfolio/team page - scroll to Interested section
      scrollToInterestedSection();
    } else {
      // On other pages - navigate to portfolio
      navigateToPath("/portfolio");
    }
  }, [isPortfolioPage, isTeamPage]);

  const handlePageClick = useCallback((path: string) => {
    if (currentPathname === normalizePath(path)) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigateToPath(path);
    }
    setIsMenuOpen(false);
  }, [currentPathname]);

  const handleContactClick = useCallback(() => {
    if (isOnHomePage) {
      scrollToContactSection();
      setActiveSection(CONTACT_SECTION_ID);
    } else {
      navigateToPath(`/#${CONTACT_SECTION_ID}`);
    }
    setIsMenuOpen(false);
  }, [isOnHomePage]);

  useTrackActiveContactSection(isOnHomePage, setActiveSection);
  useCloseMenuOnOutsideClick(isMenuOpen, navigationBarRef, setIsMenuOpen);
  useLockBodyScrollOnMobileMenu(isMenuOpen);

  return (
    <>
      <AppBar position="fixed" elevation={0} sx={getHeaderSx(isDarkMode)}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          maxWidth={MAX_WIDTH}
          width="100%"
          spacing={2}
          sx={{
            margin: "0 auto",
            paddingX: {
              xs: SIDE_PADDING.xs,
              md: SIDE_PADDING.md,
              xl: SIDE_PADDING.xl,
            },
          }}
        >
          <Box component="a" href="/" sx={logoLinkSx}>
            <SvgIcon
              component={LogoImage}
              inheritViewBox
              htmlColor={PRIMARY_COLOR}
              sx={logoImageSx}
            />
          </Box>

          <Stack direction="row" gap="2rem" alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleHeaderButtonClick}
              sx={getButtonSx(isDarkMode)}
            >
              {isPortfolioPage || isTeamPage ? "Interested?" : "Portfolio"}
            </Button>

            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={toggleMenu}
              sx={getMenuButtonSx(isDarkMode)}
            >
              {isMenuOpen ? (
                <CloseIcon sx={{ fontSize: "2rem" }} />
              ) : (
                <MenuIcon sx={{ fontSize: "2rem" }} />
              )}
            </IconButton>
          </Stack>
        </Stack>
      </AppBar>

      {/* Navigation Bar */}
      <Box
        ref={navigationBarRef}
        sx={getNavigationBarSx(isMenuOpen, isDarkMode)}
      >
        <DesktopNav
          isDarkMode={isDarkMode}
          activeSection={activeSection}
          isPageNavItemActive={isPageNavItemActive}
          onPageClick={handlePageClick}
          onContactClick={handleContactClick}
        />
        <MobileNav
          isDarkMode={isDarkMode}
          activeSection={activeSection}
          isPageNavItemActive={isPageNavItemActive}
          onPageClick={handlePageClick}
          onContactClick={handleContactClick}
        />
      </Box>
    </>
  );
}

export default Header;
